import { isValidChartRecid } from '@/composables/isValidChartRecid';
import { isValidNumber } from '@/utils/isValidNumber';
import { getPeriodOptionFromPeriodValue } from '@/enums/period-options';
import { getRangeObjFromRangeTitle, RangeOption } from '@/enums/range-options';
import { CHART_TYPE_OPTION_PERF_CHART } from '@/enums/chart-type-options';

interface SharpCharts2ImageProperties {
  i: string;
  symbol: string;
  period: string;
  chartType: string;
  range: string;
  rangeBar: string;
  rangeGap: string;
  rangeYears: string;
  rangeMonths: string;
  rangeDays: string;
  rangeStartDate: string;
  rangeEndDate: string;
  perfChartSymbolsString: string;
  useMainChartSymbolAsBaseline: boolean;
  annotationsRecid: number;
}

function getPerfChartUrlParameters(chartType: string, perfChartSymbolsString: string, useMainChartSymbolAsBaseline: boolean) {
  if (chartType !== CHART_TYPE_OPTION_PERF_CHART.value)
    return {};

  if (!useMainChartSymbolAsBaseline)
    return { compare: perfChartSymbolsString };

  return { compare: perfChartSymbolsString, perf: 'true' };
}

function getAnnotationsRecidUrlParameter(annotationsRecid: number) {
  if (!isValidChartRecid(annotationsRecid))
    return {};

  return { a: annotationsRecid.toString() };
}

function getBarAndGapUrlParameters(rangeOption: RangeOption, rangeBar: string, rangeGap: string) {
  if (!rangeOption.useBarAndGap)
    return {};

  const isValidRangeBar = isValidNumber(rangeBar, { isRequiredToBeInteger: true, isRequiredToBeGreaterThanZero: true });
  const isValidRangeGap = isValidNumber(rangeGap, { isRequiredToBeInteger: true, isRequiredToBeGreaterThanOrEqualToZero: true });

  if (isValidRangeBar && isValidRangeGap)
    return { b: rangeBar, g: rangeGap };

  if (isValidRangeBar)
    return { b: rangeBar };

  if (isValidRangeGap)
    return { g: rangeGap };

  return {};
}

function getYearMonthDayUrlParameters(rangeOption: RangeOption, rangeYears: string, rangeMonths: string, rangeDays: string) {
  if (!rangeOption.useNumberOfDays)
    return {};

  return {
    yr: rangeYears,
    mn: rangeMonths,
    dy: rangeDays
  };
}

function getStartEndUrlParameters(rangeOption: RangeOption, rangeStartDate: string, rangeEndDate: string) {
  if (!rangeOption.useStartDateAndEndDate)
    return {};

  if (rangeStartDate && rangeEndDate)
    return { st: rangeStartDate, en: rangeEndDate };

  if (rangeStartDate)
    return { st: rangeStartDate };

  if (rangeEndDate)
    return { en: rangeEndDate };

  return {};
}

export function getSharpCharts2ChartImageUrlParametersObject(areParametersAlreadyUrlEncoded: boolean, imageProperties: SharpCharts2ImageProperties): SharpCharts2UrlParameterImageData {
  const {
    i,
    symbol,
    period,
    chartType,
    range,
    rangeBar,
    rangeGap,
    rangeYears,
    rangeMonths,
    rangeDays,
    rangeStartDate,
    rangeEndDate,
    perfChartSymbolsString,
    useMainChartSymbolAsBaseline,
    annotationsRecid
  } = imageProperties;

  const rangeOption = getRangeObjFromRangeTitle(range);

  const parameterObj: SharpCharts2UrlParameterImageData = {
    s: symbol,
    p: getPeriodOptionFromPeriodValue(period).sharpCharts2Id,
    ...getBarAndGapUrlParameters(rangeOption, rangeBar, rangeGap),
    ...getYearMonthDayUrlParameters(rangeOption, rangeYears, rangeMonths, rangeDays),
    ...getStartEndUrlParameters(rangeOption, rangeStartDate, rangeEndDate),
    i,
    ...getAnnotationsRecidUrlParameter(annotationsRecid),
    ...getPerfChartUrlParameters(chartType, perfChartSymbolsString, useMainChartSymbolAsBaseline)
  };

  if (areParametersAlreadyUrlEncoded) {
    return parameterObj;
  }

  for (const key in parameterObj) {
    // @ts-ignore
    parameterObj[key] = typeof parameterObj[key] === 'string' ? encodeURIComponent(parameterObj[key]) : parameterObj[key];
  }

  return parameterObj;
}
